import axios from "axios";
import { getInvoiceForPrint } from "../api/invoiceManagerApi";
import {
  fetchParcelCostDetails,
  fetchParcelDetailsByParcelNo,
  fetchParcelProductsForEdit,
} from "../api/adminEditBookingApi";
import { buildInvoiceSlipFromPrintPayload } from "./invoiceManagerSlip";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

async function fetchLookupList(path) {
  try {
    const { data } = await axios.get(`${getApiBaseUrl()}${path}`, {
      headers: getAuthHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Resolve parcel id from a GetAllPayments row.
 * @param {object} payment
 * @returns {string|number|null}
 */
export function resolvePaymentParcelId(payment) {
  if (payment?.parcelOderNo != null && payment.parcelOderNo !== "") {
    return payment.parcelOderNo;
  }
  if (payment?.parcelOrderNo != null && payment.parcelOrderNo !== "") {
    return payment.parcelOrderNo;
  }
  if (payment?.parcelId != null && payment.parcelId !== "") {
    return payment.parcelId;
  }
  const serial = payment?.parcelNoWithRecordSerialNo;
  if (!serial) return null;
  if (String(serial).includes("~")) return String(serial).split("~")[0];
  const match = String(serial).match(/^(\d+)/);
  return match ? match[1] : serial;
}

/**
 * Build PaymentSlip localStorage payload for a payment-history row.
 * @param {object} payment
 * @returns {Promise<object|null>}
 */
export async function buildSlipFromPaymentHistoryRow(payment) {
  const invoiceNo = String(payment?.paymentInvoiceNo || "").trim();
  if (!invoiceNo) {
    throw new Error("This payment has no invoice number.");
  }

  const parcelId = resolvePaymentParcelId(payment);
  const [
    printPayload,
    costData,
    parcelDetails,
    liveProducts,
    itemCategories,
    itemTypes,
    cargoTypes,
    dimensions,
    weights,
  ] = await Promise.all([
    getInvoiceForPrint(invoiceNo),
    parcelId
      ? fetchParcelCostDetails(parcelId).catch(() => ({}))
      : Promise.resolve({}),
    parcelId
      ? fetchParcelDetailsByParcelNo(parcelId).catch(() => null)
      : Promise.resolve(null),
    parcelId
      ? fetchParcelProductsForEdit(parcelId).catch(() => [])
      : Promise.resolve([]),
    fetchLookupList("ItemCategory"),
    fetchLookupList("ItemType"),
    fetchLookupList("CargoType"),
    fetchLookupList("Dimension"),
    fetchLookupList("Weight"),
  ]);

  if (!printPayload) {
    throw new Error(`Could not load invoice ${invoiceNo}.`);
  }

  const detailObj =
    parcelDetails && !Array.isArray(parcelDetails) ? parcelDetails : {};

  return buildInvoiceSlipFromPrintPayload({
    printPayload,
    parcelRow: {
      parcelId: parcelId || printPayload.parcelId,
      parcelNo: parcelId || printPayload.parcelId,
      recordSerialNo:
        payment?.recordSerialNo ||
        String(payment?.parcelNoWithRecordSerialNo || "")
          .split("~")
          .slice(1)
          .join("~") ||
        "",
      senderName: payment?.senderName,
      senderEmail: payment?.senderEmail,
      senderPhone: payment?.senderPhone,
    },
    costData: {
      ...(costData || {}),
      ...detailObj,
    },
    invoiceRow: payment,
    invoiceHistory: [payment],
    liveProducts: Array.isArray(liveProducts) ? liveProducts : [],
    lookups: {
      itemCategories,
      itemTypes,
      cargoTypes,
      dimensions,
      weights,
    },
  });
}

/**
 * Open the payment slip page for view / download / print.
 * @param {object} payment
 * @param {"view"|"download"|"print"} mode
 */
export async function openPaymentHistoryInvoice(payment, mode = "view") {
  const slipData = await buildSlipFromPaymentHistoryRow(payment);
  if (!slipData) {
    throw new Error("Could not prepare this invoice.");
  }

  localStorage.setItem("paymentSlipData", JSON.stringify(slipData));

  const params = new URLSearchParams();
  if (mode === "download") params.set("autoDownload", "1");
  if (mode === "print") params.set("autoPrint", "1");
  const qs = params.toString();
  const url = qs ? `/paymentslip?${qs}` : "/paymentslip";
  window.open(url, "_blank");
}
